import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'app/gamma/src/types';
import classNames from 'classnames';
import {
  OverlayType,
  setOverlay,
} from 'app/gamma/src/modules/state/ui/overlay';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { preloadData } from 'app/scripts/views/organization/InviteTeamMembers/TeamIllustrationAnimation';
import { TeamType } from 'app/gamma/src/modules/state/models/teams';
import { HomeTestIds } from '@trello/webdriverio-tests';
import {
  ExpandableNavigationItemButtonProps,
  ExpandableNavigationItemIcon,
  ExpandableNavigationItemText,
} from 'app/src/components/ExpandableNavigation';
import styles from './create-team-button.less';

import { forTemplate } from 'app/common/lib/util/i18n';
import { isPaidManagedEnterpriseMember as isPaidManagedEnterpriseMemberSelector } from 'app/gamma/src/selectors/enterprises';
import { State } from 'app/gamma/src/modules/types';

const l = forTemplate('home');

interface CreateTeamButtonProps {
  fullTab?: boolean;
}

type CreateTeamButtonStateProps = ExpandableNavigationItemButtonProps & {
  isPaidManagedEnterpriseMember: boolean;
};

type CreateTeamButtonDispatchProps = ExpandableNavigationItemButtonProps & {
  onClickCreateItem?: () => void;
};

type AllProps = CreateTeamButtonProps &
  CreateTeamButtonStateProps &
  CreateTeamButtonDispatchProps;

const CreateTeamButtonUnconnected: React.FunctionComponent<AllProps> = ({
  isPaidManagedEnterpriseMember,
  onClick,
  onClickCreateItem,
  fullTab,
  ...props
}) => {
  // Just opt paid managed enterprise users out of using the teambuilder altogether
  const canShowTeamBuilder = !isPaidManagedEnterpriseMember;
  const onClickCallback = canShowTeamBuilder ? onClickCreateItem : onClick;

  useEffect(() => {
    if (canShowTeamBuilder) {
      preloadData();
    }
  });

  return (
    <button
      className={classNames(styles.createTeamButton, {
        [styles.fullTab]: fullTab,
      })}
      data-test-id={HomeTestIds.NavigationCreateTeamButton}
      onClick={onClickCallback}
      aria-label={l('home-create-team')}
      {...props}
    >
      <ExpandableNavigationItemIcon iconName="add" />
      {fullTab && (
        <ExpandableNavigationItemText>
          {l('home-create-team')}
        </ExpandableNavigationItemText>
      )}
    </button>
  );
};

const mapStateToProps = (state: State): CreateTeamButtonStateProps => {
  return {
    isPaidManagedEnterpriseMember: isPaidManagedEnterpriseMemberSelector(state),
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch,
): CreateTeamButtonDispatchProps => {
  return {
    onClickCreateItem() {
      trackUe({
        category: Noun.HOME,
        verb: Verb.OPENS,
        directObj: Noun.CREATE_TEAM_MODAL,
        method: 'by clicking create team in left sidebar',
      });

      dispatch(
        setOverlay({
          overlayType: OverlayType.CreateTeam,
          teamType: TeamType.Default,
        }),
      );
    },
  };
};

export const CreateTeamButton = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateTeamButtonUnconnected);
